import type { CallStatistic } from 'types/call.type';
import { Disposition } from 'types/call.type';
import { DISPOSITION_BAR_COLORS } from 'common/constants';
import { formatStatusPercent, type CallStatusSummaryCardVariant } from '../CallStatusSummary';
import { formatDisposition } from '../../../utils';

export type StatusMixSubRow = {
    label: string;
    count: number;
};

export type StatusMixCardData = {
    id: string;
    label: string;
    total: number;
    variant: CallStatusSummaryCardVariant;
    color: string;
    subRows: StatusMixSubRow[];
};

export type DonutSegmentData = {
    id: string;
    label: string;
    value: number;
    color: string;
};

export type DispositionBreakdownRow = {
    key: string;
    label: string;
    count: number;
    barColor: string;
    isPositive: boolean;
};

const STATUS_THEME: Record<CallStatusSummaryCardVariant, { color: string }> = {
    pending: {
        color: 'var(--slai-ui-gray-700)',
    },
    overdue: {
        color: 'var(--slai-ui-gray-700)',
    },
    inProgress: {
        color: 'var(--slai-ui-info-color)',
    },
    unable: {
        color: 'var(--slai-ui-error-color)',
    },
    contact: {
        color: 'var(--slai-ui-success-color)',
    },
    voicemail: {
        color: 'var(--slai-ui-warning-color)',
    },
    transfer: {
        color: 'var(--slai-ui-primary-purple-dark)',
    },
    avgDuration: {
        color: 'var(--slai-ui-gray-700)',
    },
};

const asCount = (value: number | null | undefined): number => value ?? 0;

export const normalizeCallStatistic = (callStats?: CallStatistic): CallStatistic => ({
    blocked: asCount(callStats?.blocked),
    pending: asCount(callStats?.pending),
    overDue: asCount(callStats?.overDue),
    dialing: asCount(callStats?.dialing),
    ringing: asCount(callStats?.ringing),
    inProgress: asCount(callStats?.inProgress),
    transferInProgress: asCount(callStats?.transferInProgress),
    humanOnCall: asCount(callStats?.humanOnCall),
    agentAbandoned: asCount(callStats?.agentAbandoned),
    userAbandoned: asCount(callStats?.userAbandoned),
    failed: asCount(callStats?.failed),
    busy: asCount(callStats?.busy),
    noAnswer: asCount(callStats?.noAnswer),
    voicemail: asCount(callStats?.voicemail),
    voicemailSuccess: asCount(callStats?.voicemailSuccess),
    voicemailFailed: asCount(callStats?.voicemailFailed),
    transferFailed: asCount(callStats?.transferFailed),
    transferCompleted: asCount(callStats?.transferCompleted),
    transferUserAbandoned: asCount(callStats?.transferUserAbandoned),
    noAgentsAvailable: asCount(callStats?.noAgentsAvailable),
    agentRejected: asCount(callStats?.agentRejected),
    total: asCount(callStats?.total),
    transfer: asCount(callStats?.transfer),
    badNumber: asCount(callStats?.badNumber),
    completed: asCount(callStats?.completed),
    contacted: asCount(callStats?.contacted),
    transferCallCenterCompleted: asCount(callStats?.transferCallCenterCompleted),
    transferCallCenterFailed: asCount(callStats?.transferCallCenterFailed),
    transferCallCenterInProgress: asCount(callStats?.transferCallCenterInProgress),
    unableToContact: callStats?.unableToContact,
    outbound: asCount(callStats?.outbound),
    inbound: asCount(callStats?.inbound),
    manual: asCount(callStats?.manual),
    dispositionBreakdown: callStats?.dispositionBreakdown ?? {},
});

const resolveUnableToContact = (stats: CallStatistic): number => {
    if (stats.unableToContact != null) return asCount(stats.unableToContact);
    return asCount(stats.failed) + asCount(stats.busy) + asCount(stats.noAnswer) + asCount(stats.badNumber);
};

/** Maps mix-panel status group ids to API `callStatus` filter values (comma-separated). */
export const STATUS_GROUP_CALL_STATUSES: Record<string, string[]> = {
    pending: ['PENDING', 'QUEUED'],
    inProgress: ['DIALING', 'RINGING', 'IN_PROGRESS'],
    unable: ['FAILED', 'BUSY', 'NO_ANSWER', 'BAD_NUMBER', 'BLOCKED'],
    contact: [
        'USER_ABANDONED',
        'AGENT_ABANDONED',
        'COMPLETED',
        'TRANSFER_CALL_CENTER_COMPLETED',
        'TRANSFER_CALL_CENTER_FAILED',
        'TRANSFER_CALL_CENTER_IN_PROGRESS',
    ],
    voicemail: ['VOICEMAIL', 'VOICEMAIL_FAILED'],
    transfer: [
        'TRANSFER_IN_PROGRESS',
        'TRANSFER_COMPLETED',
        'TRANSFER_FAILED',
        'TRANSFER_USER_ABANDONED',
        'NO_AGENTS_AVAILABLE',
        'AGENT_REJECTED',
        'HUMAN_ON_CALL',
    ],
};

export const getStatusGroupFilterValue = (statusGroupId: string): string =>
    (STATUS_GROUP_CALL_STATUSES[statusGroupId] ?? []).join(',');

const POSITIVE_DISPOSITIONS = new Set<string>([Disposition.QUALIFY, Disposition.INTERESTED]);

export const buildStatusMixCards = (
    callStats: CallStatistic | undefined,
    humanTransfer: boolean
): StatusMixCardData[] => {
    const stats = normalizeCallStatistic(callStats);
    const unableTotal = resolveUnableToContact(stats);

    const cards: Array<StatusMixCardData | null> = [
        {
            id: 'pending',
            label: 'Queued/Overdue',
            total: stats.pending + stats.overDue,
            variant: 'pending',
            ...STATUS_THEME.pending,
            subRows: [
                { label: 'Queued', count: stats.pending },
                { label: 'Overdue', count: stats.overDue },
            ],
        },
        {
            id: 'inProgress',
            label: 'In Progress',
            total: stats.dialing + stats.ringing + stats.inProgress,
            variant: 'inProgress',
            ...STATUS_THEME.inProgress,
            subRows: [
                { label: 'Dialing', count: stats.dialing },
                { label: 'Ringing', count: stats.ringing },
                { label: 'Active', count: stats.inProgress },
            ],
        },
        {
            id: 'unable',
            label: 'Unable to Contact',
            total: unableTotal,
            variant: 'unable',
            ...STATUS_THEME.unable,
            subRows: [
                { label: 'Failed', count: stats.failed },
                { label: 'Busy', count: stats.busy },
                { label: 'No answer', count: stats.noAnswer },
                { label: 'Bad number', count: stats.badNumber },
            ],
        },
        {
            id: 'contact',
            label: 'Contacted',
            total: stats.contacted,
            variant: 'contact',
            ...STATUS_THEME.contact,
            subRows: [
                { label: 'User abandoned', count: stats.userAbandoned },
                { label: 'Agent abandoned', count: stats.agentAbandoned },
                { label: 'Completed', count: stats.completed },
                { label: 'CC transfer completed', count: stats.transferCallCenterCompleted },
                { label: 'CC transfer failed', count: stats.transferCallCenterFailed },
                { label: 'CC transfer in progress', count: stats.transferCallCenterInProgress },
            ],
        },
        {
            id: 'voicemail',
            label: 'Voicemail',
            total: stats.voicemail,
            variant: 'voicemail',
            ...STATUS_THEME.voicemail,
            subRows: [
                { label: 'Success', count: stats.voicemailSuccess },
                { label: 'Failed', count: stats.voicemailFailed },
            ],
        },
        humanTransfer
            ? {
                  id: 'transfer',
                  label: 'Transfer',
                  total: stats.transfer,
                  variant: 'transfer',
                  ...STATUS_THEME.transfer,
                  subRows: [
                      { label: 'Completed', count: stats.transferCompleted },
                      { label: 'Failed', count: stats.transferFailed },
                      { label: 'In progress', count: stats.transferInProgress },
                      { label: 'No agents', count: stats.noAgentsAvailable },
                      { label: 'Agent rejected', count: stats.agentRejected },
                      { label: 'User abandoned', count: stats.transferUserAbandoned },
                      { label: 'Human on call', count: stats.humanOnCall },
                  ],
              }
            : null,
    ];

    return cards.filter((card): card is StatusMixCardData => card != null);
};

export const buildDonutSegments = (cards: StatusMixCardData[]): DonutSegmentData[] =>
    cards.map((card) => ({
        id: card.id,
        label: card.label,
        value: card.total,
        color: card.color,
    }));

export const buildDispositionBreakdown = (
    callStats: CallStatistic | undefined,
    labels?: Record<string, string>
): DispositionBreakdownRow[] => {
    const counts = callStats?.dispositionBreakdown ?? {};

    return Object.entries(counts)
        .filter(([, count]) => (count ?? 0) > 0)
        .map(([key, count]) => ({
            key,
            label: formatDisposition(key, labels),
            count: count ?? 0,
            barColor: DISPOSITION_BAR_COLORS[key] ?? 'var(--slai-ui-primary-purple)',
            isPositive: POSITIVE_DISPOSITIONS.has(key),
        }))
        .sort((a, b) => b.count - a.count);
};

export const formatAnalyticsCount = (value: number | null | undefined): string => asCount(value).toLocaleString();

export const getDispositionBarWidthPercent = (count: number, maxCount: number): number => {
    if (maxCount <= 0) return 0;
    return formatStatusPercent(count, maxCount);
};
